import { useLazyQuery } from '@apollo/client'
import {
  Button,
  ButtonBase,
  Hidden,
  Typography,
  useMediaQuery,
  useTheme
} from '@material-ui/core'
import AddIcon from '@material-ui/icons/Add'
import Pagination from '@material-ui/lab/Pagination'
import React, { useEffect, useMemo, useState } from 'react'

import PageHeader from '../../components/PageHeader'
import { USERS_LIST_QUERY } from '../../graphql/userManagement'
import { getTranslate } from '../../localization'
import { LocalizedLink } from '../../routes/localizedRoutes'
import { UserList, UserListFilters } from '../../types/users'
import UsersFilters from './UsersFilters'
import UsersTable from './UsersTable'
import UsersTableMobile from './UsersTableMobile'
import useStyles from './styles/index.styles'

const UserManagement: React.FC = () => {
  const classes = useStyles()
  const theme = useTheme()
  const isSmallScreen = useMediaQuery(theme.breakpoints.down('sm'))
  const [usersfilters, setUsersFilters] = useState<UserListFilters>({
    entityCodes: [],
    pagination: {
      page: 1,
      perPage: 6
    }
  })
  const [count, setcount] = useState(0)

  const [getUsers, { data: userList, loading, refetch }] = useLazyQuery<{
    users: UserList
  }>(USERS_LIST_QUERY, {
    notifyOnNetworkStatusChange: true,
    fetchPolicy: 'network-only',
    onCompleted: (res) => {
      setcount(res.users.pagination.total)
    }
  })

  useEffect(() => {
    getUsers({
      variables: { filters: usersfilters }
    })
  }, [
    usersfilters.pagination.page,
    usersfilters.role,
    usersfilters.entityCodes
  ])

  const numberOfPages = useMemo(
    () => Math.ceil(count / usersfilters.pagination.perPage),
    [count]
  )

  return (
    <div>
      <PageHeader title={getTranslate('User Management')} />
      <div className={classes.header}>
        <Typography variant="h3" gutterBottom>
          {getTranslate('User Management')}
        </Typography>
        <ButtonBase
          component={LocalizedLink}
          to={'/access-management/user-management/create'}>
          <Button startIcon={<AddIcon />} variant="contained" color="primary">
            {getTranslate(`Add${isSmallScreen ? '' : ' User'}`)}
          </Button>
        </ButtonBase>
      </div>
      <UsersFilters
        getUsers={getUsers}
        filters={usersfilters}
        setFilters={setUsersFilters}
      />
      <Hidden smDown>
        <UsersTable
          userData={userList?.users.users || []}
          loading={loading}
          refetch={refetch}
        />
      </Hidden>
      <Hidden mdUp>
        <UsersTableMobile
          userData={userList?.users.users || []}
          loading={loading}
          refetch={refetch}
        />
      </Hidden>
      <div className={classes.paginationContainer}>
        <Pagination
          count={numberOfPages}
          color="primary"
          size={isSmallScreen ? 'small' : 'medium'}
          page={userList?.users.pagination.page}
          onChange={(_, value) =>
            setUsersFilters({
              ...usersfilters,
              pagination: {
                ...usersfilters.pagination,
                page: value
              }
            })
          }
        />
      </div>
    </div>
  )
}

export default UserManagement
